import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useCreateOrganization } from '@/features/organizations/hooks/useCreateOrganization'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Layers } from 'lucide-react'

const createOrganizationSchema = z.object({
  name: z
    .string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be 50 characters or less')
    .regex(/^[a-z0-9_-]+$/, 'Name can only contain lowercase letters, numbers, hyphens (-), and underscores (_)'),
  displayName: z
    .string()
    .min(1, 'Display name is required')
    .max(100, 'Display name must be 100 characters or less'),
  domain: z
    .string()
    .max(100, 'Domain must be 100 characters or less')
    .optional()
    .or(z.literal('')),
  isActive: z.boolean().default(true),
})

type CreateOrganizationForm = z.infer<typeof createOrganizationSchema>

interface CreateOrganizationForTenantDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tenantId: string
  tenantName: string
  onSuccess?: () => void
}

export function CreateOrganizationForTenantDialog({
  open,
  onOpenChange,
  tenantId,
  tenantName,
  onSuccess
}: CreateOrganizationForTenantDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { mutate: createOrganization } = useCreateOrganization()

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
    setValue
  } = useForm<CreateOrganizationForm>({
    resolver: zodResolver(createOrganizationSchema),
    defaultValues: {
      name: '',
      displayName: '',
      domain: '',
      isActive: true,
    },
  })

  const isActive = watch('isActive')

  const onSubmit = async (data: CreateOrganizationForm) => {
    setIsSubmitting(true)

    try {
      const submitData = {
        name: data.name,
        displayName: data.displayName,
        domain: data.domain || undefined,
        isActive: data.isActive,
        tenantId: tenantId, // Pre-set tenant ID
      }

      createOrganization(submitData, {
        onSuccess: () => {
          reset()
          onSuccess?.()
        },
        onSettled: () => {
          setIsSubmitting(false)
        }
      })
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  const handleClose = () => {
    if (!isSubmitting) {
      reset()
      onOpenChange(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create Organization</DialogTitle>
          <DialogDescription>
            Create a new organization within this tenant.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Tenant indicator */}
          <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50 border">
            <Layers className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Tenant:</span>
            <Badge variant="secondary">{tenantName}</Badge>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              placeholder="e.g., example-org"
              {...register('name')}
              disabled={isSubmitting}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Lowercase letters, numbers, hyphens (-), and underscores (_) only
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="displayName">Display Name *</Label>
            <Input
              id="displayName"
              placeholder="e.g., Example Organization"
              {...register('displayName')}
              disabled={isSubmitting}
            />
            {errors.displayName && (
              <p className="text-sm text-destructive">{errors.displayName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="domain">Domain (Optional)</Label>
            <Input
              id="domain"
              placeholder="e.g., example.com"
              {...register('domain')}
              disabled={isSubmitting}
            />
            {errors.domain && (
              <p className="text-sm text-destructive">{errors.domain.message}</p>
            )}
            <p className="text-xs text-muted-foreground">
              Must be unique across all organizations
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="isActive"
              checked={isActive}
              onCheckedChange={(checked) => setValue('isActive', checked)}
              disabled={isSubmitting}
            />
            <Label htmlFor="isActive">Active</Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Organization'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
